import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Inbox, Loader2, Search, Check, CheckCheck, ClipboardList, CheckCircle2, RotateCcw, X, User, Phone, Wallet, MapPin, Signal, Save } from "lucide-react";
import { api, WS_BASE } from "@/lib/api";
import { sourceLabel, sourceShort, formatTime, contactDisplay } from "@/lib/format";

const SOURCE_FILTERS = [
  { value: "all", label: "Всі" },
  { value: "web", label: "Сайт" },
  { value: "app", label: "Додаток" },
  { value: "telegram", label: "Телеграм" },
  { value: "closed", label: "Завершені" },
];

let _audioCtx;
function playDing() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.start();
    o.stop(ctx.currentTime + 0.32);
  } catch (e) {}
}

function TaskModal({ defaultTitle, onSubmit, onClose }) {
  const [title, setTitle] = useState(defaultTitle || "");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("normal");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="task-modal" onClick={onClose}>
      <div className="bg-white rounded-[14px] shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg">Нова задача з чату</h3>
          <button onClick={onClose} data-testid="task-modal-close" className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="chat-task-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Назва задачі" className="w-full border border-slate-200 rounded-[10px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <input data-testid="chat-task-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Виконавець" className="w-full border border-slate-200 rounded-[10px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <select data-testid="chat-task-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]">
            <option value="low">Низький</option>
            <option value="normal">Звичайний</option>
            <option value="high">Високий</option>
          </select>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-slate-200 rounded-[10px] py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Скасувати</button>
          <button data-testid="chat-task-submit" disabled={!title.trim()} onClick={() => onSubmit({ title: title.trim(), assignee: assignee.trim(), priority })} className="flex-1 bg-[#0F76BC] hover:bg-[#0c5f96] text-white rounded-[10px] py-2.5 text-sm font-semibold transition-colors disabled:opacity-50">Створити</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminInbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [activeCid, setActiveCid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [subscriber, setSubscriber] = useState(null);
  const [savingSub, setSavingSub] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const activeCidRef = useRef(null);
  const wsRef = useRef(null);
  const messagesRef = useRef(null);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chats");
      setChats(data.chats);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  const openChat = useCallback(async (cid) => {
    setActiveCid(cid);
    activeCidRef.current = cid;
    setLoadingThread(true);
    setSubscriber(null);
    api.get(`/admin/subscribers/by-cid/${encodeURIComponent(cid)}`)
      .then(({ data }) => setSubscriber(data.subscriber))
      .catch(() => setSubscriber(null));
    try {
      const { data } = await api.get(`/admin/chats/${encodeURIComponent(cid)}`);
      setMessages(data.messages);
      loadChats();
    } finally {
      setLoadingThread(false);
    }
  }, [loadChats]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  useEffect(() => {
    const cid = searchParams.get("cid");
    if (cid) {
      openChat(cid);
      searchParams.delete("cid");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const total = chats.reduce((s, c) => s + (c.unread || 0), 0);
    document.title = total > 0 ? `(${total}) IPPLUS CRM` : "IPPLUS CRM";
    return () => { document.title = "IPPLUS CRM"; };
  }, [chats]);

  // WebSocket for real-time updates
  useEffect(() => {
    const token = localStorage.getItem("ipplus_admin_token");
    if (!token) return;
    let closed = false;
    const connect = () => {
      const ws = new WebSocket(`${WS_BASE}/admin/ws?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        const data = JSON.parse(evt.data);
        if (data.type === "read" && data.by === "client") {
          const rcid = `${data.source}:${data.contact_id}`;
          if (rcid === activeCidRef.current) {
            setMessages((prev) => prev.map((x) => (x.sender === "admin" ? { ...x, read_by_client: true } : x)));
          }
          return;
        }
        if (data.type !== "message") return;
        const m = data.message;
        const cid = `${m.source}:${m.contact_id}`;
        if (cid === activeCidRef.current) {
          setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          if (m.sender === "client") {
            api.get(`/admin/chats/${encodeURIComponent(cid)}`).catch(() => {});
          }
        } else if (m.sender === "client") {
          toast.info(`Нове повідомлення (${sourceLabel(m.source)})`);
        }
        if (m.sender === "client") playDing();
        loadChats();
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      closed = true;
      wsRef.current?.close();
    };
  }, [loadChats]);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !activeCid) return;
    setSending(true);
    setText("");
    try {
      const { data } = await api.post(`/admin/chats/${encodeURIComponent(activeCid)}`, { text: t });
      setMessages((prev) => (prev.some((x) => x.id === data.message.id) ? prev : [...prev, data.message]));
      loadChats();
    } catch {
      toast.error("Не вдалося надіслати");
      setText(t);
    } finally {
      setSending(false);
    }
  };

  const activeChat = chats.find((c) => c.cid === activeCid);
  const filteredChats = chats.filter((c) => {
    if (filterSource === "closed") {
      if (!c.closed) return false;
    } else {
      if (c.closed) return false;
      if (filterSource !== "all" && c.source !== filterSource) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        contactDisplay(c).toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.last_text || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const setChatClosed = async (closed) => {
    if (!activeCid) return;
    try {
      await api.post(`/admin/chats/${encodeURIComponent(activeCid)}/status`, { closed });
      setChats((prev) => prev.map((c) => (c.cid === activeCid ? { ...c, closed } : c)));
      toast.success(closed ? "Чат завершено" : "Чат відновлено");
    } catch {
      toast.error("Не вдалося змінити статус чату");
    }
  };

  const saveSubscriber = async () => {
    if (!subscriber?.id) return;
    setSavingSub(true);
    try {
      const { data } = await api.put(`/admin/subscribers/${subscriber.id}`, {
        name: subscriber.name || null,
        phone: subscriber.phone || null,
        tariff: subscriber.tariff || null,
        balance: subscriber.balance === "" || subscriber.balance == null ? null : Number(subscriber.balance),
        address: subscriber.address || null,
      });
      setSubscriber(data.subscriber);
      toast.success("Картку абонента збережено");
    } catch {
      toast.error("Не вдалося зберегти картку");
    } finally {
      setSavingSub(false);
    }
  };

  const createTaskFromChat = async ({ title, assignee, priority }) => {
    try {
      await api.post("/admin/tasks", {
        title,
        assignee: assignee || null,
        priority,
        cid: activeCid,
        source: activeChat?.source,
        contact_id: activeChat?.contact_id,
        contact_name: activeChat ? contactDisplay(activeChat) : null,
      });
      toast.success("Задачу створено з чату");
      setShowTaskModal(false);
    } catch {
      toast.error("Не вдалося створити задачу");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] grid-rows-[100%] h-full min-h-0 overflow-hidden bg-slate-50">
      {/* LEFT PANE */}
      <aside className={`h-full min-h-0 overflow-hidden border-r border-slate-200 bg-white flex-col ${activeCid ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 py-3 border-b border-slate-100 space-y-3">
          <h2 className="font-head font-bold tracking-tight text-slate-800">Вхідні звернення</h2>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="chat-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Пошук за ім'ям, телефоном, текстом..."
              className="w-full border border-slate-200 rounded-[10px] pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow]"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="channel-filters">
            {SOURCE_FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`filter-${f.value}`}
                onClick={() => setFilterSource(f.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  filterSource === f.value ? "bg-[#0F76BC] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="chat-list">
          {filteredChats.length === 0 && (
            <div className="p-8 text-center text-slate-400 text-sm">
              <Inbox size={32} className="mx-auto mb-2 opacity-40" />
              {chats.length === 0 ? "Поки немає діалогів" : "Нічого не знайдено"}
            </div>
          )}
          {filteredChats.map((c) => (
            <button
              key={c.cid}
              data-testid={`chat-item-${c.cid}`}
              onClick={() => openChat(c.cid)}
              className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors flex gap-3 relative ${activeCid === c.cid ? "bg-[#0F76BC]/5" : ""}`}
            >
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-semibold shrink-0 uppercase">
                {(contactDisplay(c)[0] || "?")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-slate-800 truncate">{contactDisplay(c)}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{formatTime(c.last_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 shrink-0">
                    {sourceShort(c.source)}
                  </span>
                  <span className="text-xs text-slate-500 truncate">
                    {c.last_sender === "admin" ? "Ви: " : ""}{c.last_text}
                  </span>
                </div>
              </div>
              {c.unread > 0 && (
                <span data-testid={`unread-badge-${c.cid}`}
                  className="shrink-0 self-center min-w-5 h-5 px-1.5 rounded-full bg-[#0F76BC] text-white text-[11px] font-bold flex items-center justify-center">
                  {c.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>

      {/* RIGHT PANE */}
      <section className={`h-full min-h-0 overflow-hidden flex-col bg-slate-50 ${activeCid ? "flex" : "hidden md:flex"}`}>
        {!activeCid ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <div className="w-20 h-20 rounded-[20px] bg-white border border-slate-200 flex items-center justify-center mb-4 shadow-sm">
              <MessageSquare size={34} className="text-[#0F76BC]/40" />
            </div>
            <p className="font-head font-semibold text-slate-500">Оберіть діалог</p>
            <p className="text-sm">Виберіть звернення зі списку ліворуч</p>
          </div>
        ) : (
          <>
            <header className="min-h-16 border-b border-slate-200 bg-white flex items-center px-4 gap-3 shrink-0 flex-wrap py-2">
              <button onClick={() => { setActiveCid(null); activeCidRef.current = null; }}
                data-testid="thread-back-btn"
                className="md:hidden w-9 h-9 rounded-[10px] hover:bg-slate-100 flex items-center justify-center text-slate-500">
                <ArrowLeft size={18} />
              </button>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-semibold uppercase shrink-0">
                {activeChat ? contactDisplay(activeChat)[0] : "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
                  {activeChat ? contactDisplay(activeChat) : ""}
                  {activeChat?.closed && (
                    <span data-testid="closed-badge" className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-600">Завершено</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#0F76BC]/10 text-[#0F76BC]">
                    {sourceLabel(activeChat?.source)}
                  </span>
                  {activeChat?.phone && <span>{activeChat.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <button data-testid="create-task-from-chat-btn" onClick={() => setShowTaskModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-semibold text-[#0F76BC] border border-[#0F76BC]/30 hover:bg-[#0F76BC]/5 transition-colors">
                  <ClipboardList size={16} /> <span className="hidden sm:inline">Створити задачу</span>
                </button>
                {activeChat?.closed ? (
                  <button data-testid="reopen-chat-btn" onClick={() => setChatClosed(false)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
                    <RotateCcw size={16} /> <span className="hidden sm:inline">Відновити</span>
                  </button>
                ) : (
                  <button data-testid="close-chat-btn" onClick={() => setChatClosed(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors">
                    <CheckCircle2 size={16} /> <span className="hidden sm:inline">Завершити чат</span>
                  </button>
                )}
              </div>
            </header>

            <div className="flex-1 min-h-0 flex">
              <div className="flex-1 min-h-0 flex flex-col">
            <div ref={messagesRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 md:p-6 flex flex-col gap-3" data-testid="thread-messages">
              {loadingThread && (
                <div className="flex justify-center py-4"><Loader2 className="animate-spin text-slate-300" /></div>
              )}
              {messages.map((m) => (
                <div key={m.id}
                  className={`max-w-[80%] md:max-w-[70%] p-3.5 rounded-[12px] shadow-sm text-sm leading-relaxed ${
                    m.sender === "client"
                      ? "bg-[#0F76BC] text-white self-end rounded-br-none"
                      : "bg-white border border-slate-200 text-slate-800 self-start rounded-bl-none"
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[10px] mt-1.5 flex items-center gap-1 ${m.sender === "client" ? "text-white/70 justify-end" : "text-slate-400"}`}>
                    <span>{formatTime(m.created_at)}</span>
                    {m.sender === "admin" && (
                      m.read_by_client
                        ? <CheckCheck size={13} className="text-[#0F76BC]" data-testid={`read-${m.id}`} />
                        : <Check size={13} className="text-slate-400" data-testid={`sent-${m.id}`} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="p-3 md:p-4 bg-white border-t border-slate-200 shrink-0 flex gap-2">
              <input
                data-testid="reply-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Напишіть відповідь..."
                className="flex-1 border border-slate-200 rounded-[12px] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow] bg-white"
              />
              <button
                data-testid="reply-send-btn"
                type="submit"
                disabled={sending || !text.trim()}
                className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-5 rounded-[12px] font-semibold transition-[background-color,transform] active:scale-95 shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </form>
              </div>
              <aside data-testid="subscriber-card" className="hidden lg:flex lg:flex-col w-80 shrink-0 border-l border-slate-200 bg-white overflow-y-auto">
                <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                  <User size={16} className="text-[#0F76BC]" />
                  <h3 className="font-head font-bold text-slate-800">Картка абонента</h3>
                </div>
                {subscriber ? (
                  <div className="p-4 space-y-3.5">
                    {[
                      { key: "name", label: "Ім'я", icon: User, type: "text" },
                      { key: "phone", label: "Телефон", icon: Phone, type: "text" },
                      { key: "tariff", label: "Тариф", icon: Signal, type: "text" },
                      { key: "balance", label: "Баланс, грн", icon: Wallet, type: "number" },
                      { key: "address", label: "Адреса підключення", icon: MapPin, type: "text" },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1"><f.icon size={13} /> {f.label}</label>
                        <input
                          data-testid={`sub-${f.key}`}
                          type={f.type}
                          value={subscriber[f.key] ?? ""}
                          onChange={(e) => setSubscriber({ ...subscriber, [f.key]: e.target.value })}
                          className="w-full border border-slate-200 rounded-[10px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow]"
                        />
                      </div>
                    ))}
                    <button data-testid="save-subscriber-btn" onClick={saveSubscriber} disabled={savingSub}
                      className="w-full bg-[#0F76BC] hover:bg-[#0c5f96] text-white rounded-[10px] py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                      {savingSub ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Зберегти
                    </button>
                  </div>
                ) : (
                  <div className="p-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Завантаження...</div>
                )}
              </aside>
            </div>
          </>
        )}
      </section>

      {showTaskModal && (
        <TaskModal
          defaultTitle={activeChat ? `Звернення: ${contactDisplay(activeChat)}` : ""}
          onSubmit={createTaskFromChat}
          onClose={() => setShowTaskModal(false)}
        />
      )}
    </div>
  );
}
